import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Star, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReviewsList() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews/list`, { withCredentials: true });
        setReviews(data);
      } catch (err) {
        console.error("Failed:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const avg = reviews.length > 0 ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1) : "0";

  return (
    <div data-testid="reviews-list" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Star className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" /> Customer Reviews
        </h2>
        <div className="flex items-center gap-3">
          <Badge className="bg-amber-500/20 text-amber-400 text-lg px-3 py-1">{avg} / 5</Badge>
          <span className="text-zinc-500 text-sm">{reviews.length} reviews</span>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-center py-12">Loading...</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16">
          <Star className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400">No reviews yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {reviews.map((r) => (
            <Card key={r.id} className="bg-zinc-900 border-zinc-800">
              <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} className={`w-4 h-4 ${s <= r.rating ? "text-amber-400 fill-amber-400" : "text-zinc-700"}`} />
                      ))}
                    </div>
                    <span className="text-xs text-zinc-500">{r.order_number}</span>
                    <Badge className={r.rating >= 4 ? "bg-green-500/20 text-green-400" : r.rating >= 3 ? "bg-yellow-500/20 text-yellow-400" : "bg-red-500/20 text-red-400"}>
                      {r.rating}/5
                    </Badge>
                  </div>
                  <p className="text-sm text-white">{r.customer_name || r.customer_email}</p>
                  {r.feedback && (
                    <p className="text-xs text-zinc-400 mt-1 flex items-start gap-1">
                      <MessageSquare className="w-3 h-3 mt-0.5 flex-shrink-0" /> {r.feedback}
                    </p>
                  )}
                </div>
                <span className="text-xs text-zinc-600">{new Date(r.created_at).toLocaleDateString()}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
